import React, { useEffect, useState, useRef } from "react";
import EmailDatabase from "./emailDatabase";
import Utils from "../../../../utility";
import { useDispatch, useSelector } from "react-redux";
import {
  hideLoader,
  removeUser,
  showLoader
} from "../../../../slices/userSlice";
import RewardsService from "../../../../services/reward";
import { failureMessageConstant, keyConstants } from "../../../../constants";

const Email = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10; // page limit
  const [totalItems, setTotalItems] = useState(0);
  const [dataCountTillPage, setDataCountTillPage] = useState(0);
  const [marketingUsers, setMarketingUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState();
  const [discount, setDiscount] = useState();
  const [dates, setDates] = useState({ start: null, end: null });
  const [calenderOpen, setCalenderOpen] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [isAscending, setIsAscending] = useState(true);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isApplyCount, setIsApplyCount] = useState(0);

  const discountDropdownRef = useRef(null);
  const calendarDropDownRef = useRef(null);

  const { IS_REFRESH_TOKEN_LOADING } = keyConstants;

  const fetchMarketingUsers = async (skip, limit, isDatesCleared) => {
    const tokenStatus = await Utils.checkTokenExpireOrNot(fetchMarketingUsers);
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }
    try {
      let requestData = {
        businessId: user?._id,
        searchQuery: searchQuery || "",
        discount:
          discount === keyConstants.ALL_DISCOUNTS ? null : discount || null,
        startTime: (!isDatesCleared && dates?.start) || null,
        endTime: (!isDatesCleared && dates?.end) || null,
        sortingKey: isAscending ? "asc" : "des",
        skip,
        limit
      };
      let response = await new RewardsService().getMarketingUsers(requestData);
      setMarketingUsers(response?.data);
      setTotalItems(response?.total);
      setDataCountTillPage(itemsPerPage * (page - 1));
    } catch (error) {
      Utils.failureToastMessage(error?.message);
    }
  };

  const fetchMarketingDiscounts = async () => {
    const tokenStatus = await Utils.checkTokenExpireOrNot(
      fetchMarketingDiscounts
    );
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) {
        setIsLoading(true);
        return;
      }
      dispatch(removeUser({}));
      return;
    }
    try {
      setIsLoading(true);
      const response = await new RewardsService().getMarketingDiscounts(
        user?._id
      );
      response.sort((a, b) => a - b);
      response.splice(0, 0, keyConstants.ALL_DISCOUNTS);
      setDiscounts(response);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      Utils.failureToastMessage(error?.message);
    }
  };

  const downloadCSV = (data) => {
    if (data?.length === 0) {
      Utils.failureToastMessage(failureMessageConstant.NO_EMAILS_SUBMITTED_YET);
      return;
    }
    const columnNames = ["Email", "Submitted On", "Discount Value"];
    let csvContent =
      "data:text/csv;charset=utf-8," +
      columnNames.join(",") +
      "\n" +
      data
        ?.map((user) => {
          const addedOnDate = new Date(user.addedOn);
          const formattedDate = addedOnDate.toLocaleDateString("en-US");
          const formattedTime = addedOnDate.toLocaleTimeString("en-US");
          const discountValue =
            user.discountValue && Utils.isPurelyNumeric(user.discountValue)
              ? `${user.discountValue}%`
              : user.discountValue || "N/A";
          const row = [
            user.email,
            `${formattedDate} ${formattedTime}`,
            discountValue
          ]; // Combine date and time
          return row.join(",");
        })
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "marketing_users.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = async () => {
    const tokenStatus = await Utils.checkTokenExpireOrNot(handleExport);
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) {
        dispatch(showLoader());
        return;
      }
      dispatch(removeUser({}));
      return;
    }
    try {
      if (totalItems < 1) {
        Utils.failureToastMessage("No data to export");
      } else {
        let requestData = {
          businessId: user?._id,
          searchQuery: searchQuery || "",
          discount:
            discount === keyConstants.ALL_DISCOUNTS ? null : discount || null,
          startTime: dates?.start || null,
          endTime: dates?.end || null,
          sortingKey: isAscending ? "asc" : "des",
          skip: 0,
          limit: totalItems
        };
        dispatch(showLoader());
        let response = await new RewardsService().getMarketingUsers(
          requestData
        );
        downloadCSV(response?.data);
      }
    } catch (error) {
      Utils.failureToastMessage(error?.message);
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    fetchMarketingDiscounts();
  }, []);

  useEffect(() => {
    fetchMarketingUsers((page - 1) * itemsPerPage, itemsPerPage, false);
  }, [page, searchQuery, discount, isFilterApplied, isAscending]);
  useEffect(() => {
    if (isApplyCount > 1) {
      fetchMarketingUsers((page - 1) * itemsPerPage, itemsPerPage, false);
    }
  }, [isApplyCount]);

  return (
    <EmailDatabase
      marketingUsers={marketingUsers}
      discounts={discounts}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      discount={discount}
      setDiscount={setDiscount}
      dates={dates}
      setDates={setDates}
      calenderOpen={calenderOpen}
      setCalenderOpen={setCalenderOpen}
      isAscending={isAscending}
      setIsAscending={setIsAscending}
      isLoading={isLoading}
      page={page}
      setPage={setPage}
      itemsPerPage={itemsPerPage}
      totalItems={totalItems}
      discountDropdownRef={discountDropdownRef}
      dataCountTillPage={dataCountTillPage}
      handleExport={handleExport}
      setIsFilterApplied={setIsFilterApplied}
      calendarDropDownRef={calendarDropDownRef}
      setIsApplyCount={setIsApplyCount}
      isApplyCount={isApplyCount}
    />
  );
};

export default Email;
